import { useState, useContext, useEffect } from "react";
import API from "api/api";
import { AppContext } from "context/AppContext";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { getGoogleAuthSecret } from "api/security";

import { Colors, FontSizes, MediaQueries, Spacing } from "@paclife/plds.tokens";
import { Button } from "@paclife/plds.button";
import Typography from "common/Typography/Typography";
import BaseModal from "common/Modal/BaseModal";
import TextField from "common/TextField/TextField";
import Message from "common/Message/Message";
import validator from "utils/validator";


const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ContentLt = styled.div`
    display: flex;
    flex-direction: column;
`;

const Title = styled(Typography)`
    color: ${Colors.primary};
    margin: ${Spacing[16]} 0 ${Spacing[24]};
    // tablet and mobile
    @media (max-width: ${MediaQueries.lg}) {
        margin: ${Spacing[8]} 0;
    }
`;

const ContentCont = styled(Typography)`
    margin-bottom: ${Spacing[8]};
    text-align: left;

    a {
        margin-bottom: ${Spacing[16]};
        color: ${Colors.neutral500};
    }
`;

const ContentLabel = styled(Typography)`
    margin-bottom: 0;
    text-align: left;
`;

const ImgCont = styled.div`
    margin-top: ${Spacing[4]};

    img {
        width: 115px;
        height: 115px;
        padding: 5px;
        border: 1px solid ${Colors.neutral300};
    }
`;

const TextFieldSecret = styled(TextField)`
    input {
        background: ${Colors.neutral100};
        color: ${Colors.neutral400};
        font-size: ${FontSizes[900]};
        text-align: center;
        margin-bottom: ${Spacing[4]};
    }
`;

const DigitsContainer = styled.div`
    span { 
        margin: auto;
    }
`;

const TextFieldDigits = styled(TextField)`
    input {
        width: 50%;
        margin: auto;
        text-align: center;
        font-size: ${FontSizes[1500]};
        letter-spacing: ${Spacing[4]};;
    }
`;

const Toggle = styled.div`
    text-decoration: underline;
    color: ${Colors.neutral500};
    cursor: pointer;
    margin-bottom: ${Spacing[16]};
`;

const CancelButton = styled(Button)`
    width: 108px;
`;

const SubmitButton = styled(Button)`
    width: 108px;
`;


const AuthenticatorModal = ({ closeModal }) => {
    const {
        fetchProfileData,
        dispatch,
        personal
    } = useContext(AppContext);

    const { t } = useTranslation("authenticatorModal");
    const [loading, setLoading] = useState(false);
    const [digits, setDigits] = useState('');
    const [showQR, setShowQR] = useState(true);
    const [secret, setSecret] = useState("");
    const [showSecret, setShowSecret] = useState(false);
    const [disableBtn, setDisableBtn] = useState(true);
    const [verifyError, setVerifyError] = useState("");
    const [formatError, setFormatError] = useState("");

    useEffect(() => {
        getSecret();
    }, []);

    const handleCancel = async () => {
        closeModal();
    }

    const getSecret = async () => {
        const { succeed, data } = await getGoogleAuthSecret();
        if (succeed) {
            setSecret(data);
        } else {
            setSecret('Error');
        }
    }

    const dispSecretToggle = () => {
        if (showQR) {
            setShowSecret(true);
            setShowQR(false);
        } else {
            setShowSecret(false);
            setShowQR(true);
        }
    }

    const AlternateLink = () => {
        return (
            <Toggle onClick={dispSecretToggle} title={t(`setup.auth.toggleAlt`)}>
                { (showQR) ? t(`setup.auth.cantscan`) : t(`setup.auth.backtoqr`) }
            </Toggle>
        )
    }

    const Secret = () => {
        return  (
            <ImgCont>
                <TextFieldSecret
                    id="secret"
                    name="secret"
                    value={secret}
                    readonly="readonly"
                /> 
            </ImgCont>
        )
    }

    const handleDigits = async (e) => {
        const val = e.target.value;
        const valid = validate(val);
        if (valid) {
            setDigits(val);
            setFormatError(false);
            setDisableBtn(false);
        } else {
            setFormatError(true);
            setDisableBtn(true)
        }
    };

    const validate = (value) => {
        return validator.SecurityCode(value);
    };

    const handleUpdate = async () => {
        setLoading(true);
        const factorType = "token:software:totp";
        const val = personal.username;
        const { succeed, message } = await API.activateFactor({
            factorType,
            factorValue: val,
            activationCode: digits
        });
        setLoading(false);
        if (!succeed) {
            setVerifyError(message);
        } else {
            dispatch({
                type: "FACTOR",
                payload: {
                    [factorType]: {
                        id: null,
                        factorValue: val,
                        status: "ACTIVE"
                    }
                }
            });
            fetchProfileData();
            closeModal();
        }
    };

    return (
        <BaseModal
            size="default"
            secondaryButton={
                <CancelButton
                    variant="secondaryNeutral"
                    onClick={handleCancel}
                    data-track="analytics"
                    data-bname="close-edit-cemail-modal"
                >
                    {t(`setup.button.secondary`)}
                </CancelButton>
            }
            primaryButton={
                <SubmitButton
                    onClick={handleUpdate}
                    disabled={disableBtn}
                    data-track="analytics"
                    data-bname={"setup-auth"}
                >
                    {t(`setup.button.primary`)}
                </SubmitButton>
            }
            closeModal={closeModal}
            loading={loading}
        >
            <Content>
                <Title variant="h2" display="block" align="center">
                    {t(`setup.auth.title`)}
                </Title>
            </Content>
            <ContentLt>
                <ContentCont variant="body">
                    {t(`setup.auth.download`)}
                </ContentCont>
                { (showQR) && 
                    <>
                        <ContentCont variant="body">
                            {t(`setup.auth.phone`)}
                        </ContentCont>
                        <ContentCont variant="body">
                            {t(`setup.auth.qrCode`)}
                        </ContentCont>
                        <ContentCont variant="body">
                            {t(`setup.auth.finish`)}
                        </ContentCont>
                    </>
                }
                { (showSecret) && 
                    <>
                        <ContentCont variant="body">
                            {t(`setup.auth.secret`)}
                        </ContentCont>
                        <ContentCont variant="body">
                            {t(`setup.auth.finish`)}
                        </ContentCont>  
                    </>
                }
            </ContentLt>
            <Content>
                { (showQR) &&    
                    <ImgCont>
                        <img src="/profile/user/security/topt/image" 
                            id="qrcode" 
                            alt="QR Code" 
                        />
                    </ImgCont>
                }
                { (showSecret) && 
                    <>
                        <ContentLabel variant="body">
                            {t(`setup.auth.setupcode`)}
                        </ContentLabel>  
                        <Secret /> 
                    </>
                }
                <ContentCont variant="microcopy">
                    <AlternateLink />
                </ContentCont>
                <ContentCont variant="body">
                    {t(`setup.auth.digits`)}
                </ContentCont>
                <DigitsContainer>
                    <TextFieldDigits
                        autoFocus
                        id="digits"
                        name="digits"
                        value={""}
                        onChange={handleDigits}
                        maxLength={"6"}
                        error={formatError}
                        errorMessage= {t(`setup.auth.digitsError`)}
                    />
                </DigitsContainer>
                { verifyError && (
                    <Message type="error" message={verifyError} />
                )}  
            </Content>
        </BaseModal>
    );
};

export default AuthenticatorModal;